import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { AssetListTable } from './Common';
import { useTranslation } from 'react-i18next';
import { formatCreatedDate } from '../constants/utils';
import api from '../helpers/api';
import AssetModal from './AssetModal';
import LocationModal from './LocationModal';

const AssetTypeModal = ({ isOpen, toggle, assetTypeData, loading, onEditAssetType = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null });
  const [locationModal, setLocationModal] = useState({ isOpen: false, data: null });

  // Fetch assets using this asset type
  const fetchAssets = async () => {
    if (!assetTypeData?.id) return;

    setAssetsLoading(true);
    try {
      const { data } = await api.get(`organization-asset-type/get-all/assets?assetTypeId=${assetTypeData.id}`);
      const allAssets = data?.response || [];
      setAssets(allAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'assets' && assetTypeData?.id) {
      fetchAssets();
    }
  }, [activeTab, assetTypeData?.id]);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
    }
  }, [isOpen]);

  if (!assetTypeData && !loading) return null;

  return (
    <>
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Enhanced Header */}
        <div
          className="modal-header border-0 bg-gradient-primary text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem'
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-purchase-tag text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {assetTypeData?.name || t('business.components.asset_type_modal.title_default', 'Asset Type Details')}
              </h5>
              <p className="text-white-50 small mb-0">{t('business.components.asset_type_modal.subtitle', 'Asset type information and details')}</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditAssetType && assetTypeData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditAssetType(assetTypeData)}
                title={t('shared.edit_asset_type', 'Edit Asset Type')}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t('shared.edit_asset_type', 'Edit Asset Type')}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t('shared.close', 'Close')}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Nav
          tabs
          className="border-bottom-0 px-4 pt-3 bg-light"
          style={{ borderRadius: '0' }}
        >
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'assets' ? 'active' : ''}`}
              onClick={() => setActiveTab('assets')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'assets' ? '#fff' : 'transparent',
                color: activeTab === 'assets' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'assets' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-package me-2"></i>
              {t('shared.assets', 'Assets')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Modal Body */}
        <div
          className="modal-body p-0"
          style={{
            maxHeight: 'calc(90vh - 200px)',
            overflow: 'auto'
          }}
        >
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info Tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-purchase-tag text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t('business.components.asset_type_modal.info_title', 'Asset Type Information')}</h5>
                        <p className="text-muted small mb-0">{t('business.components.asset_type_modal.info_subtitle', 'Complete asset type details')}</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">{t('business.components.asset_type_modal.loading_details', 'Loading asset type details...')}</p>
                      </div>
                    ) : (
                      assetTypeData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.name', 'Name')}</label>
                                <div className="fw-medium">{assetTypeData?.name || 'N/A'}</div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">{t('shared.description', 'Description')}</label>
                                <div className="fw-medium">{assetTypeData?.description || t('shared.no_description', 'No description')}</div>
                              </div>
                            </div>
                          </Col>

                          {/* Activity */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-time me-2"></i>{t('shared.activity', 'Activity')}
                              </h6>
                              {assetTypeData?.createdAt && (
                                <div className="info-item mb-3">
                                  <div className="fw-medium text-muted">
                                    {t('shared.created_by_on', 'Created {{user}} on {{date}}', {
                                      user: assetTypeData?.createdByUser?.name ? `${t('shared.by', 'By')} ${assetTypeData.createdByUser.name} ` : '',
                                      date: formatCreatedDate(assetTypeData?.createdAt)
                                    })}
                                  </div>
                                </div>
                              )}
                              {assetTypeData?.updatedAt && assetTypeData?.createdAt !== assetTypeData?.updatedAt && (
                                <div className="info-item">
                                  <div className="fw-medium text-muted">
                                    {t('shared.last_updated_by_on', 'Last updated {{user}} on {{date}}', {
                                      user: assetTypeData?.updatedByUser?.name ? `${t('shared.by', 'By')} ${assetTypeData.updatedByUser.name} ` : '',
                                      date: formatCreatedDate(assetTypeData?.updatedAt)
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Assets Tab */}
            <TabPane tabId="assets">
              <div className="p-4">
                <AssetListTable
                  data={assets}
                  loading={assetsLoading}
                  loadingMessage={t('business.components.asset_type_modal.loading_assets', 'Loading assets...')}
                  customEmptyState={
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="bx bx-package text-muted" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <h5 className="text-muted mb-2">{t('business.components.asset_type_modal.no_assets', 'No assets yet')}</h5>
                      <p className="text-muted mb-4">
                        {t('business.components.asset_type_modal.no_assets_desc', "This asset type hasn't been assigned to any assets yet.")}
                      </p>
                      <div className="alert alert-light border d-inline-block text-start">
                        <div className="d-flex align-items-start">
                          <i className="bx bx-bulb text-warning me-2 mt-1"></i>
                          <div>
                            <strong>{t('business.components.asset_type_modal.how_to_use', 'How to use this asset type:')}</strong>
                            <ol className="mb-0 mt-2 ps-3">
                              <li>{t('business.components.asset_type_modal.how_to_use_step_1', 'Go to Assets')}</li>
                              <li>{t('business.components.asset_type_modal.how_to_use_step_2', 'Create a new asset or edit an existing one')}</li>
                              <li>{t('business.components.asset_type_modal.how_to_use_step_3', 'Select "{{name}}" from the Asset Type dropdown', {
                                name: assetTypeData?.name || ''
                              })}</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  onAssetClick={(asset) => setAssetModal({ isOpen: true, userData: asset })}
                  onLocationClick={(asset) => setLocationModal({ isOpen: true, data: asset })}
                />
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            {t('shared.close', 'Close')}
          </button>
        </div>
      </motion.div>
    </Modal>

    {assetModal.isOpen && (
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal({ isOpen: false, userData: null })}
        userData={assetModal.userData}
        loading={false}
        formatCreatedDate={formatCreatedDate}
      />
    )}

    {locationModal.isOpen && (
      <LocationModal
        isOpen={locationModal.isOpen}
        toggle={() => setLocationModal({ isOpen: false, data: null })}
        title={locationModal.data?.currentLocation}
        mainData={locationModal.data?.currentLocation}
        formatCreatedDate={formatCreatedDate}
      />
    )}
    </>
  );
};

export default AssetTypeModal;
